/**
 * ARIA Warranty Lookup Page
 * Version: 2.0.0
 *
 * Searches the WarrantyHistory table via GIN full-text index.
 * Plain-language query across customerName, address, sku, and status.
 */

import { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { Search, FileText, Package, MapPin, User, Calendar, Hash, Wrench } from 'lucide-react';
import { clsx } from 'clsx';
import type { WarrantyHistoryResult } from '../types';
import { isUnauthenticatedError } from '../config/apollo';
import { SortableHeader } from '../components/table/SortableHeader';
import { WarrantyBomPanel } from '../components/warranty/WarrantyBomPanel';
import type { SortDirection } from '../utils/sort';

const PAGE_SIZE = 50;

const WARRANTY_HISTORY_SEARCH_QUERY = gql`
  query WarrantyHistorySearch($query: String!, $limit: Int, $offset: Int, $sortField: String, $sortDirection: SortDirection) {
    warrantyHistorySearch(query: $query, limit: $limit, offset: $offset, sortField: $sortField, sortDirection: $sortDirection) {
      totalCount
      results {
        id
        customerName
        address
        sku
        installDate
        status
        poNumber
        jobDescription
        serviceOrder
        referencedPaNumber
        quoteNumber
        quoteName
        rank
      }
    }
  }
`;

const STATUS_STYLES: Record<string, string> = {
  ACTIVE:  'bg-green-100 text-green-700',
  EXPIRED: 'bg-gray-100 text-gray-700',
  VOID:    'bg-red-100 text-red-700',
  // Product line has no defined warranty term (e.g. outside the 1500/6500
  // series matrix) — deliberately distinct from ACTIVE/EXPIRED so it reads
  // as "verify manually," not as a computed answer.
  UNKNOWN: 'bg-amber-100 text-amber-800',
};

export default function Warranty() {
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [allResults, setAllResults] = useState<WarrantyHistoryResult[]>([]);
  const [selected, setSelected] = useState<WarrantyHistoryResult | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirectionState] = useState<SortDirection>('asc');

  const handleSort = (field: string) => {
    setSortDirectionState((prev) => (sortField === field && prev === 'asc' ? 'desc' : 'asc'));
    setSortField(field);
    setOffset(0);
  };

  // Pre-fill search when navigated from chat ("View in Warranties" button)
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setSearchValue(q);
      setSubmittedQuery(q);
    }
  }, [searchParams]);

  const { data, loading, error } = useQuery(WARRANTY_HISTORY_SEARCH_QUERY, {
    variables: {
      query: submittedQuery,
      limit: PAGE_SIZE,
      offset,
      sortField: sortField || undefined,
      sortDirection: sortField ? sortDirection.toUpperCase() : undefined,
    },
    skip: !submittedQuery,
  });

  const totalCount: number = data?.warrantyHistorySearch?.totalCount ?? 0;

  useEffect(() => {
    const incoming = data?.warrantyHistorySearch?.results;
    if (!incoming) return;
    if (offset === 0) {
      setAllResults(incoming);
    } else {
      setAllResults((prev) => [...prev, ...incoming]);
    }
  }, [data, offset]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      setOffset(0);
      setSubmittedQuery(searchValue.trim());
    }
  };

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Warranty Lookup</h1>
        <p className="text-gray-600">
          Search by customer name, address, SKU, or any combination
        </p>
      </header>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="card p-4 mb-6">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="e.g. Johnson Calgary, 123 Maple, AWW-1500..."
              className="input pl-10 w-full"
            />
          </div>
          <button type="submit" className="btn-primary" disabled={!searchValue.trim()}>
            Search
          </button>
        </div>
      </form>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Searching warranty records...</div>
      ) : error ? (
        // An expired session already redirects to /login with its own
        // explanation (see apollo.ts's errorLink) — that happens almost
        // immediately, so this just needs to not show misleading "try again"
        // advice in the brief moment before the redirect takes effect.
        isUnauthenticatedError(error) ? null : (
          <div className="text-center py-12 text-red-500">
            Error loading results. Please try again.
          </div>
        )
      ) : submittedQuery && allResults.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p>No warranty records found</p>
          <p className="text-sm mt-1">Try a different search term</p>
        </div>
      ) : allResults.length > 0 ? (
        <div className="card overflow-hidden">
          {/* Result count */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold">{allResults.length}</span> of{' '}
              <span className="font-semibold">{totalCount}</span> results
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <SortableHeader
                    label={<span className="flex items-center gap-1"><User className="w-3 h-3" />Customer</span>}
                    field="customerName" activeField={sortField} activeDirection={sortDirection} onSort={handleSort}
                  />
                  <SortableHeader
                    label={<span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Address</span>}
                    field="address" activeField={sortField} activeDirection={sortDirection} onSort={handleSort}
                  />
                  <SortableHeader
                    label={<span className="flex items-center gap-1"><Package className="w-3 h-3" />Item Number</span>}
                    field="sku" activeField={sortField} activeDirection={sortDirection} onSort={handleSort}
                  />
                  <SortableHeader
                    label={<span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Order Date</span>}
                    field="installDate" activeField={sortField} activeDirection={sortDirection} onSort={handleSort}
                  />
                  <SortableHeader
                    label={<span className="flex items-center gap-1"><Hash className="w-3 h-3" />PO #</span>}
                    field="poNumber" activeField={sortField} activeDirection={sortDirection} onSort={handleSort}
                  />
                  <SortableHeader
                    label={<span className="flex items-center gap-1"><Wrench className="w-3 h-3" />Order Number</span>}
                    field="serviceOrder" activeField={sortField} activeDirection={sortDirection} onSort={handleSort}
                  />
                  <SortableHeader label="Description" field="jobDescription" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Status" field="status" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {allResults.map((row) => (
                  <tr
                    key={row.id}
                    className={clsx('cursor-pointer hover:bg-gray-50', selected?.id === row.id && 'bg-blue-50')}
                    onClick={() => setSelected(row)}
                    onKeyDown={(e) => {
                      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setSelected(row);
                      }
                    }}
                    tabIndex={0}
                    title="Click to view the bill of materials"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{row.customerName}</td>
                    <td className="px-4 py-3 text-gray-700">{row.address ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{row.sku ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {row.installDate
                        ? new Date(row.installDate).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.poNumber ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{row.serviceOrder ?? '—'}</div>
                      {row.quoteName && (
                        <div className="text-xs text-gray-500">Quote: {row.quoteName}</div>
                      )}
                      {row.quoteNumber && (
                        <div className="text-xs text-gray-500">Quote #: {row.quoteNumber}</div>
                      )}
                      {row.referencedPaNumber && (
                        <div className="text-xs text-gray-500">PA ref: {row.referencedPaNumber}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={row.jobDescription ?? undefined}>
                      {row.jobDescription ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      {row.status ? (
                        <span className={clsx('badge', STATUS_STYLES[row.status] ?? 'bg-gray-100 text-gray-700')}>
                          {row.status}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load more */}
          {allResults.length < totalCount && (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <button
                className="btn-secondary text-sm"
                onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                disabled={loading}
              >
                Load more ({totalCount - allResults.length} remaining)
              </button>
            </div>
          )}
        </div>
      ) : null}
      {selected && <WarrantyBomPanel key={selected.id} record={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
